import React from 'react';

import { Tooltip } from 'antd';

import { useUserPermission } from '@contexts/UserContext';

import { NwButton, NwRemoveButton } from '@components/Gui/NwButton';
import NwIcon from '@components/Gui/NwIcon';

import { useStatementContext } from '../StatementContext';
import { Actions } from './PayableStyles';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

/**
 * PayableActions
 *
 * Action bar for payable statements (create invoice, add to invoice, payment, remove taxes)
 * Note: "Create Invoice", "Add to Invoice" and "Remove Taxes" are only available for model contacts
 */
const PayableActions = ({
    selectedList,
    disableInvoiceButton,
    disablePaymentButton,
    disableRemoveTaxesButton,
    invoiceButtonErrors,
    paymentButtonErrors,
    removeTaxesButtonErrors,
    selectedStatementsTotal,
    handleCreateInvoice,
    handleAddToInvoice,
    handleCreatePayment,
    handleRemoveTaxes,
}) => {
    const { contactType } = useStatementContext();
    const accessAccounting = useUserPermission('AccessAccounting');
    const isModel = contactType === 'model';
    const TooltipContent = ({ type }) => {
        if (type === 'invoice') {
            return (
                <div>
                    <p>You can't create an invoice:</p>
                    <ul>
                        {invoiceButtonErrors.map((err, index) => (
                            <li key={index}>{err}</li>
                        ))}
                    </ul>
                </div>
            );
        }
        if (type === 'payment') {
            return (
                <div>
                    <p>You can't create a payment</p>
                    <ul>
                        {paymentButtonErrors.map((err, index) => (
                            <li key={index}>{err}</li>
                        ))}
                    </ul>
                </div>
            );
        }
        if (type === 'removeTaxes') {
            return (
                <div>
                    <p>You can't remove taxes</p>
                    <ul>
                        {removeTaxesButtonErrors.map((err, index) => (
                            <li key={index}>{err}</li>
                        ))}
                    </ul>
                </div>
            );
        }
    };

    return (
        <Actions>
            {`${selectedList.length} rows selected`}
            {isModel && (
                <div className="statement-action-wrapper">
                    <NwButton
                        primary
                        icon={byPrefixAndName.fasl['file-invoice-dollar']}
                        disabled={!selectedList || selectedList.length === 0 || disableInvoiceButton}
                        solid
                        size="small"
                        label="create invoice"
                        onClick={handleCreateInvoice}
                    />
                    {accessAccounting && (
                        <NwButton
                            primary
                            icon={byPrefixAndName.fasl['file-circle-plus']}
                            disabled={!selectedList || selectedList.length === 0 || disableInvoiceButton}
                            solid
                            size="small"
                            label="add to existing invoice draft"
                            onClick={handleAddToInvoice}
                        />
                    )}
                    {invoiceButtonErrors.length > 0 && (
                        <Tooltip placement="right" title={<TooltipContent type="invoice" />}>
                            <NwIcon danger icon={byPrefixAndName.fasl['exclamation-triangle']} />
                        </Tooltip>
                    )}
                </div>
            )}
            <div className="statement-action-wrapper">
                <NwButton
                    primary
                    icon={byPrefixAndName.fasl['money-check-dollar-pen']}
                    disabled={!selectedList || selectedList.length === 0 || disablePaymentButton}
                    solid
                    size="small"
                    label={`create payment${selectedStatementsTotal ? `: ${selectedStatementsTotal}` : ''}`}
                    onClick={handleCreatePayment}
                />
                {paymentButtonErrors.length > 0 && (
                    <Tooltip placement="right" title={<TooltipContent type="payment" />}>
                        <NwIcon danger icon={byPrefixAndName.fasl['exclamation-triangle']} />
                    </Tooltip>
                )}
            </div>
            {isModel && (
                <div className="statement-action-wrapper">
                    <NwRemoveButton
                        primary
                        icon={byPrefixAndName.fasl['xmark']}
                        disabled={!selectedList || selectedList.length === 0 || disableRemoveTaxesButton}
                        solid
                        size="small"
                        label="remove taxes"
                        onClick={handleRemoveTaxes}
                        needConfirmation
                        confirmationLabel="You are permanently removing all taxes from selected rows"
                        confirmationPlacement="top"
                        confirmationButtonLabel="Remove"
                    />
                    {removeTaxesButtonErrors.length > 0 && (
                        <Tooltip placement="right" title={<TooltipContent type="removeTaxes" />}>
                            <NwIcon danger icon={byPrefixAndName.fasl['exclamation-triangle']} />
                        </Tooltip>
                    )}
                </div>
            )}
            <span>&nbsp;</span>
        </Actions>
    );
};

export default PayableActions;
